import type { CompanyDto, MailboxDto } from '@bqcrm/shared';
import { Alert, Badge, Button, Group, NumberInput, PasswordInput, Stack, Switch, Text, TextInput } from '@mantine/core';
import { IconMailbox } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { formatDateTime } from '../format';
import { notifyOk } from '../notify';
import { QueryState } from './QueryState';

export const useMailbox = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ['mailbox', companyId], queryFn: () => api<MailboxDto>(`/companies/${companyId}/mailbox`), enabled });

export const mailboxBadge = (m: MailboxDto | undefined, failed = false) =>
  failed ? (
    <Badge variant="light" color="red" size="sm">
      Skrzynka: nie udało się sprawdzić
    </Badge>
  ) : !m ? (
    <Badge variant="light" color="gray" size="sm">
      Skrzynka: …
    </Badge>
  ) : !m.enabled ? (
    <Badge variant="light" color="gray" size="sm">
      Skrzynka odbiorcza: wyłączona
    </Badge>
  ) : (
    <Badge variant="light" color={m.status === 'ok' ? 'green' : m.status === 'blad' ? 'red' : 'gray'} size="sm">
      Skrzynka · {m.username} · {m.status === 'ok' ? 'działa' : m.status === 'blad' ? 'błąd' : 'nie sprawdzono'}
    </Badge>
  );

/**
 * Skrzynka odbiorcza firmy (IMAP, docs/21): maile od klientów trafiają do ich kartotek. Hasło tylko do zapisu
 * (szyfrowane na serwerze, nigdy nie wraca do przeglądarki).
 */
export function MailboxSettings({ company }: { company: CompanyDto }) {
  const qc = useQueryClient();
  const q = useMailbox(company.id);
  const [enabled, setEnabled] = useState(false);
  const [host, setHost] = useState('');
  const [port, setPort] = useState<number>(993);
  const [tls, setTls] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [folder, setFolder] = useState('INBOX');
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    setEnabled(q.data.enabled);
    setHost(q.data.host);
    setPort(q.data.port);
    setTls(q.data.tls);
    setUsername(q.data.username || company.email);
    setFolder(q.data.folder);
  }, [q.data, company.email]);

  const url = `/companies/${company.id}/mailbox`;
  const save = useMutation({
    mutationFn: async () => {
      await api<MailboxDto>(url, { method: 'PUT', body: { enabled, host: host.trim(), port, tls, username: username.trim(), password: password || undefined, folder: folder.trim() || 'INBOX' } });
      return host.trim() ? api<{ ok: boolean; message: string }>(`${url}/check`, { method: 'POST' }) : null;
    },
    onSuccess: (r) => {
      setPassword('');
      setError(null);
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['mailbox', company.id] });
      if (!r || r.ok) notifyOk(enabled ? 'Zapisano — skrzynka będzie sprawdzana co 2 minuty' : 'Zapisano ustawienia skrzynki');
    },
    onError: (e) => setError(errorText(e)),
  });
  const test = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>(`${url}/check`, { method: 'POST' }),
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['mailbox', company.id] });
    },
    onError: (e) => setError(errorText(e)),
  });

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data && (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Skrzynka, na którą klienci odpisują (zwykle adres firmy: {company.email || 'brak e-maila w danych firmy'}). Co 2 minuty CRM pobiera nowe maile; mail od adresu zapisanego u klienta (albo
            u jego osoby kontaktowej) trafia do kartoteki klienta → Korespondencja, z powiadomieniem. Maile od nieznanych adresów są pomijane. W skrzynce nic się nie zmienia.
          </Text>
          {!q.data.keyConfigured && (
            <Alert color="red" title="Brak klucza szyfrowania">
              Serwer nie ma ustawionego INTEGRATIONS_KEY — hasła nie da się zapisać.
            </Alert>
          )}
          <Switch label="Pobieraj maile z tej skrzynki" checked={enabled} onChange={(e) => setEnabled(e.currentTarget.checked)} />
          <Group grow wrap="wrap" align="flex-start">
            <TextInput label="Serwer IMAP" placeholder="imap.example.com" value={host} onChange={(e) => setHost(e.currentTarget.value)} />
            <NumberInput label="Port" value={port} onChange={(v) => setPort(Number(v) || 993)} min={1} max={65535} w={{ base: '100%', sm: 110 }} style={{ flexGrow: 0 }} />
          </Group>
          <Switch label="Połączenie szyfrowane (SSL/TLS)" checked={tls} onChange={(e) => setTls(e.currentTarget.checked)} />
          <TextInput label="Login" value={username} onChange={(e) => setUsername(e.currentTarget.value)} />
          <PasswordInput
            label="Hasło"
            description={q.data.passwordSet ? `Zapisane ${formatDateTime(q.data.passwordSetAt)} — wpisz nowe tylko, aby je zmienić` : 'Przy Gmailu / Microsoft 365 — hasło aplikacji'}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            autoComplete="new-password"
          />
          <TextInput label="Folder" value={folder} onChange={(e) => setFolder(e.currentTarget.value)} />
          {q.data.importSince && (
            <Text size="xs" c="dimmed">
              Pobierane są maile od {formatDateTime(q.data.importSince)} (wcześniejsze zostają tylko w skrzynce).
            </Text>
          )}
          {q.data.lastCheckAt && (
            <Text size="xs" c={q.data.status === 'blad' ? 'red' : 'dimmed'}>
              Ostatnie sprawdzenie {formatDateTime(q.data.lastCheckAt)}: {q.data.status === 'blad' ? q.data.lastError : 'w porządku'}
            </Text>
          )}
          {result && (
            <Alert color={result.ok ? 'green' : 'red'} icon={<IconMailbox size={16} />}>
              {result.message}
            </Alert>
          )}
          {error && <Alert color="red">{error}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => test.mutate()} loading={test.isPending} disabled={!q.data.passwordSet}>
              Sprawdź połączenie
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending}>
              Zapisz
            </Button>
          </Group>
        </Stack>
      )}
    </QueryState>
  );
}
